import { useEffect, useMemo, useState } from "react";
import {
  IconCash,
  IconChartBar,
  IconClockHour4,
  IconCpu,
  IconSparkles,
} from "@tabler/icons-react";
import { Text } from "../../../../components/common/typography";
import { SectionCard } from "../../../../components/common/section-card";
import { StatCard } from "../../../../components/common/stat-card";
import { Chip } from "../../../../components/common/chip";
import { Pagination } from "../../../../components/common/pagination";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { Select } from "../../../../components/forms/select";
import { SearchBar } from "../../../../components/common/input";
import {
  adminAiExtractionsService,
  type AdminAiExtractionStats,
  type AdminAiExtractionListItem,
  type AdminAiExtractionsQuery,
} from "../../../api_client";
import { AiExtractionWeeklyChart } from "./ai-extraction-weekly-chart";
import { formatDateTime, formatDuration, formatNumber, formatUSD } from "../utils/format";
import { Td, Th } from "../utils/format-helpers";

const PAGE_SIZE = 25;

function statusTone(status: AdminAiExtractionListItem["status"]) {
  if (status === "success") return "approved" as const;
  if (status === "failed") return "rejected" as const;
  return "secondary" as const;
}

export function AiExtractionsTab() {
  const [stats, setStats] = useState<AdminAiExtractionStats | null>(null);
  const [statsError, setStatsError] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"" | "success" | "failed" | "pending">("");
  const [kindFilter, setKindFilter] = useState("");

  const [listData, setListData] = useState<{
    items: AdminAiExtractionListItem[];
    totalCount: number;
    pageCount: number;
  } | null>(null);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, kindFilter]);

  useEffect(() => {
    let cancelled = false;
    adminAiExtractionsService
      .stats()
      .then((res) => {
        if (cancelled) return;
        setStats(res.data);
      })
      .catch(() => {
        if (cancelled) return;
        setStatsError("Failed to load extraction stats.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setListLoading(true);
    setListError(null);
    const query: AdminAiExtractionsQuery = {
      page,
      pageSize: PAGE_SIZE,
      sortBy: "createdAt",
      sortOrder: "desc",
    };
    if (debouncedSearch) query.search = debouncedSearch;
    if (statusFilter) query.status = statusFilter;
    if (kindFilter) query.kind = kindFilter;
    adminAiExtractionsService
      .list(query)
      .then((res) => {
        if (cancelled) return;
        setListData({
          items: res.data.data,
          totalCount: res.data.meta.totalCount,
          pageCount: res.data.meta.pageCount,
        });
      })
      .catch(() => {
        if (cancelled) return;
        setListError("Failed to load extractions.");
      })
      .finally(() => {
        if (cancelled) return;
        setListLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, debouncedSearch, statusFilter, kindFilter]);

  const kindOptions = useMemo(() => {
    const kinds = new Set<string>();
    stats?.byKind.forEach((k) => kinds.add(k.kind));
    listData?.items.forEach((i) => kinds.add(i.kind));
    return [
      { value: "", label: "All kinds" },
      ...Array.from(kinds).map((k) => ({ value: k, label: k })),
    ];
  }, [stats, listData]);

  return (
    <>
      {statsError ? (
        <Text variant="body" tone="body" className="!text-accentError">{statsError}</Text>
      ) : !stats ? (
        <ShimmerLoader message="Loading AI extraction stats…" />
      ) : (
        <>
          <div className="grid grid-cols-4 gap-4 max-md:grid-cols-2 max-md:gap-3 max-sm:grid-cols-1">
            <StatCard
              icon={<IconCash size={22} />}
              label="Spend last 7 days"
              value={formatUSD(stats.last7Days.totalCostUSD)}
              sub={`${formatNumber(stats.last7Days.totalCalls)} calls · ${(stats.last7Days.successRate * 100).toFixed(0)}% success`}
            />
            <StatCard
              icon={<IconCash size={22} />}
              label="Spend last 30 days"
              value={formatUSD(stats.last30Days.totalCostUSD)}
              sub={`${formatNumber(stats.last30Days.totalCalls)} calls · ${(stats.last30Days.successRate * 100).toFixed(0)}% success`}
            />
            <StatCard
              icon={<IconCpu size={22} />}
              label="Tokens (last 30d)"
              value={formatNumber(stats.last30Days.totalTokens)}
              sub={`${formatUSD(stats.allTime.totalCostUSD)} spend all-time`}
              iconTone="light"
            />
            <StatCard
              icon={<IconClockHour4 size={22} />}
              label="Avg success duration"
              value={formatDuration(stats.last30Days.avgDurationMs)}
              sub={`${formatNumber(stats.last30Days.failedCalls)} failed of ${formatNumber(stats.last30Days.totalCalls)}`}
              iconTone="light"
            />
          </div>

          <div className="grid grid-cols-5 gap-4 max-md:grid-cols-1 max-md:gap-3">
            <SectionCard shadow title="Cost by week (last 12 weeks)" className="col-span-3 gap-3 max-md:col-span-1 max-md:px-4">
              <AiExtractionWeeklyChart data={stats.byWeek} />
            </SectionCard>

            <SectionCard shadow title="Top users (30d)" className="col-span-2 gap-2 max-md:col-span-1 max-md:px-4">
              {stats.topUsers.length === 0 ? (
                <Text variant="bodySm" tone="muted">No activity in the last 30 days.</Text>
              ) : (
                <div className="flex max-h-[180px] flex-col divide-y divide-borderDefault overflow-y-auto">
                  {stats.topUsers.map((u) => (
                    <div key={u.userId ?? Math.random()} className="flex shrink-0 items-center justify-between gap-3 py-2">
                      <div className="min-w-0 flex flex-col">
                        <Text variant="bodySmBold" tone="dark" className="truncate">
                          {u.fullName ?? u.email ?? u.userId ?? "Unknown user"}
                        </Text>
                        {u.fullName && u.email ? (
                          <Text variant="bodyXs" tone="muted" className="truncate">{u.email}</Text>
                        ) : null}
                      </div>
                      <div className="shrink-0 text-right">
                        <Text variant="bodySmBold" tone="dark">{formatUSD(u.costUSD)}</Text>
                        <Text variant="bodyXs" tone="muted">{formatNumber(u.calls)} calls</Text>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>
          </div>

          <SectionCard shadow title="By kind" className="gap-3 max-md:px-4">
            {stats.byKind.length === 0 ? (
              <Text variant="bodySm" tone="muted">No extractions yet.</Text>
            ) : (
              <div className="grid grid-cols-3 gap-3 max-md:grid-cols-1">
                {stats.byKind.map((k) => (
                  <div key={k.kind} className="flex items-center gap-3 rounded-xl border border-borderDefault px-4 py-3">
                    <IconSparkles size={20} className="text-accentCyan700" />
                    <div className="flex min-w-0 flex-1 flex-col">
                      <Text variant="bodySmBold" tone="dark" className="truncate">{k.kind}</Text>
                      <Text variant="bodyXs" tone="muted">
                        {formatNumber(k.calls)} calls · {formatNumber(k.totalTokens)} tokens
                      </Text>
                    </div>
                    <Text variant="bodySmBold" tone="dark">{formatUSD(k.costUSD)}</Text>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        </>
      )}

      <SectionCard shadow title="Recent extractions" className="gap-4 max-md:px-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[280px] flex-1 max-sm:min-w-0 max-sm:basis-full">
            <SearchBar
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by file name, error, or resource id…"
            />
          </div>
          <div className="w-[180px] max-sm:min-w-0 max-sm:flex-1">
            <Select
              name="ai-extraction-status"
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
              options={[
                { value: "", label: "All statuses" },
                { value: "success", label: "Success" },
                { value: "failed", label: "Failed" },
                { value: "pending", label: "Pending" },
              ]}
            />
          </div>
          <div className="w-[180px] max-sm:min-w-0 max-sm:flex-1">
            <Select
              name="ai-extraction-kind"
              label="Kind"
              value={kindFilter}
              onChange={(e) => setKindFilter(e.target.value)}
              options={kindOptions}
            />
          </div>
        </div>

        {listError ? (
          <Text variant="body" tone="body" className="!text-accentError">{listError}</Text>
        ) : listLoading && !listData ? (
          <ShimmerLoader message="Loading extractions…" />
        ) : !listData || listData.items.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-12">
            <IconChartBar size={32} className="text-textMuted" />
            <Text variant="body" tone="muted">No extractions match these filters.</Text>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-bgLight">
                <tr>
                  <Th>When</Th>
                  <Th>Kind</Th>
                  <Th>User</Th>
                  <Th>Resource</Th>
                  <Th>Status</Th>
                  <Th align="right">Tokens</Th>
                  <Th align="right">Cost</Th>
                  <Th align="right">Duration</Th>
                </tr>
              </thead>
              <tbody>
                {listData.items.map((row) => (
                  <tr key={row.id} className="border-t border-borderDefault">
                    <Td>
                      <Text variant="bodySm" tone="dark">{formatDateTime(row.createdAt)}</Text>
                      {row.sourceFileName ? (
                        <Text variant="bodyXs" tone="muted" className="truncate">{row.sourceFileName}</Text>
                      ) : null}
                    </Td>
                    <Td>
                      <Text variant="bodySm" tone="dark">{row.kind}</Text>
                      <Text variant="bodyXs" tone="muted">{row.provider} · {row.model}</Text>
                    </Td>
                    <Td>
                      <Text variant="bodySm" tone="dark" className="truncate">
                        {row.user.fullName ?? row.user.email ?? "—"}
                      </Text>
                      {row.user.fullName && row.user.email ? (
                        <Text variant="bodyXs" tone="muted" className="truncate">{row.user.email}</Text>
                      ) : null}
                    </Td>
                    <Td>
                      <Text variant="bodySm" tone="dark">
                        {row.resourceType ?? "—"}
                        {row.resourceId ? ` #${row.resourceId}` : ""}
                      </Text>
                    </Td>
                    <Td>
                      <Chip variant={statusTone(row.status)}>{row.status}</Chip>
                      {row.errorMessage ? (
                        <span title={row.errorMessage}>
                          <Text variant="bodyXs" tone="muted" className="truncate">
                            {row.errorMessage.slice(0, 60)}
                          </Text>
                        </span>
                      ) : null}
                    </Td>
                    <Td align="right">
                      <Text variant="bodySm" tone="dark">{row.totalTokens != null ? formatNumber(row.totalTokens) : "—"}</Text>
                    </Td>
                    <Td align="right">
                      <Text variant="bodySm" tone="dark">{row.costUSD != null ? formatUSD(row.costUSD) : "—"}</Text>
                    </Td>
                    <Td align="right">
                      <Text variant="bodySm" tone="dark">{formatDuration(row.durationMs)}</Text>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {listData && listData.pageCount > 1 ? (
          <div className="flex flex-col items-center gap-2">
            <Pagination
              page={page}
              pageCount={listData.pageCount}
              onPageChange={setPage}
            />
            <Text variant="bodySm" tone="muted">
              {formatNumber(listData.totalCount)} total · page {page} of {listData.pageCount}
            </Text>
          </div>
        ) : null}
      </SectionCard>
    </>
  );
}
